import type { ReactNode } from 'react';

/**
 * The DataGrid's public contract, ported from TMS `src/components/Grid.tsx`.
 *
 * It lives in its own module rather than inside the component because the
 * formula engine needs `GridColumn` and must not import a 3800-line React
 * component to get a type.
 *
 * Names are English here where TMS had Spanish (`tipoCelda` → `cellType`,
 * `fijaDerecha` → `pinRight`, `controlPropio` → `ownControl`). The behaviour
 * each one describes is unchanged — see `docs/Grid/GRID.md`.
 */

/**
 * How a cell is aligned and wrapped. Inferred from the data when a column does
 * not declare it: mostly numeric → `number`, mostly `dd/mm/yyyy` → `date`,
 * anything with a renderer or a dropdown → `chip`, otherwise `text`.
 */
export type CellType = 'text' | 'chip' | 'number' | 'date' | 'smartchip' | 'autofill' | 'checkbox' | 'attachment' | 'barcode' | 'text_document' | 'pivot_table' | 'reconciliation';

export type TabsPlacement = 'top' | 'bottom' | 'left';
export const DEFAULT_TABS_PLACEMENT: TabsPlacement = 'top';

export type CellAlign = 'left' | 'center' | 'right';

export interface CellRule {
  readonly align: CellAlign;
  /** When false the content stays on one line and is clipped with an ellipsis. */
  readonly wrap: boolean;
  /** Visible lines while wrapping; past this the cell scrolls internally. */
  readonly lines: number;
  /** How content that does not fit is shown, as the format bar's wrap buttons.
   *  Unset keeps the older rule: `wrap` or `clip`, and numbers expand. */
  readonly wrapMode?: CellWrapMode;
}

export type CellWrapMode = 'wrap' | 'clip' | 'shrink' | 'expand' | 'overflow';
export const CELL_WRAP_MODES: readonly CellWrapMode[] = ['wrap', 'clip', 'shrink', 'expand', 'overflow'];

export type GridAlignment = Record<CellType | 'header', CellRule>;

/**
 * Numbers are the exception to `wrap`: a number always stays on one line,
 * complete, never truncated — the grid shrinks padding, then font size, then
 * makes the remainder the column's minimum width. See `docs/Grid/GRID.md`.
 */
export const DEFAULT_ALIGNMENT: GridAlignment = {
  header: { align: 'left', wrap: true, lines: 4 },
  text: { align: 'left', wrap: true, lines: 6 },
  chip: { align: 'left', wrap: true, lines: 6 },
  number: { align: 'right', wrap: true, lines: 6 },
  date: { align: 'right', wrap: true, lines: 6 },
  smartchip: { align: 'left', wrap: true, lines: 6 },
  autofill: { align: 'left', wrap: true, lines: 6 },
  checkbox: { align: 'center', wrap: false, lines: 1 },
  attachment: { align: 'left', wrap: false, lines: 1 },
  barcode: { align: 'left', wrap: false, lines: 1 },
  text_document: { align: 'left', wrap: true, lines: 6 },
  pivot_table: { align: 'left', wrap: false, lines: 1 },
  reconciliation: { align: 'left', wrap: false, lines: 1 },
};

export const MIN_LINES = 1;
export const MAX_LINES = 20;

/** Every cell type, in the order the settings screen lists them. */
export const CELL_TYPES: readonly CellType[] = ['text', 'chip', 'number', 'date', 'smartchip', 'autofill', 'checkbox', 'attachment', 'barcode', 'text_document'];

export interface CellParams<T> {
  value: unknown;
  data?: T | undefined;
  /**
   * Index within `rows`. Needed by renderers in the embedded variant, which
   * write back into the parent form's array.
   */
  rowIndex?: number | undefined;
  /**
   * The value as text under the column's configured data format, defined only
   * when the column has one. A `cellRenderer` that draws the value must show
   * this instead of its own, so the shared format also governs cells that paint
   * themselves.
   */
  formatted?: string | undefined;
  /**
   * Inverse of `formatted`: turns what the user typed into a number, honouring
   * the locale's separators and the format's `%`. Always defined, so a renderer
   * bringing its own input parses input exactly like the grid does.
   */
  parseFormatted?: ((text: string) => number | null) | undefined;
  /**
   * The cell is being edited. A renderer with `ownControl` must draw its input
   * only when this is true and plain text otherwise — an always-live input
   * swallows the click that should merely select the cell.
   */
  editing?: boolean | undefined;
  /** The key that started the edit, when editing began by typing over the cell. */
  initialText?: string | undefined;
  /** Closes the editor and returns focus to the cell (Enter, Escape or blur). */
  stopEditing?: (() => void) | undefined;
}

/** One option of a `dropdown` column: the stored value and the visible text. */
export interface DropdownOption {
  readonly value: string;
  readonly label: string;
}

export interface DropdownConfig<T> {
  /** Memoize this — it can hold thousands of items. */
  readonly options: readonly DropdownOption[];
  readonly onChange: (value: string, rowIndex: number, row?: T) => void;
  /** Placeholder while the cell is empty. */
  readonly placeholder?: string;
  /** Label of the first option, the one that clears the cell. */
  readonly emptyLabel?: string;
}

/** An action a column offers on its cells' context menu. */
export interface GridCellAction {
  key: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  /** Resolves to the value to write into the cell, or null to leave it. A thrown error's message is shown. */
  run: () => Promise<string | number | null>;
}

export interface GridColumn<T> {
  field?: string;
  headerName: string;
  /** Tooltip on the header. */
  description?: string;
  minWidth?: number;
  /** Whether this column can be edited; a predicate may limit it per row. */
  editable?: boolean | ((row: T) => boolean);
  /** Inline editor becomes a `<select>` over these options. */
  cellEditorOptions?: readonly string[];
  /** Custom inline editor; takes precedence over `cellEditorOptions`. */
  cellEditor?: (params: {
    value: unknown;
    data?: T | undefined;
    rowIndex?: number | undefined;
    initialText?: string | undefined;
    confirm: (value: string) => void;
    cancel: () => void;
  }) => ReactNode;
  /**
   * How to display the raw value. Also feeds global search and the column
   * filter's unique values, so it must return human-readable text, never HTML.
   */
  valueFormatter?: (params: CellParams<T>) => string;
  /** For columns computed from several fields. A column with no `field` is never editable. */
  valueGetter?: (params: { data?: T }) => unknown;
  /** Extra items on the context menu of this column's cells, for the row they are in. */
  cellActions?: (row: T) => readonly GridCellAction[];
  /**
   * When this cell's value last changed, if the caller knows better than the grid
   * does (a price feed's own history). `undefined` falls back to the grid's record.
   */
  changedAt?: (row: T) => Date | null | undefined;
  /**
   * The subtotal and totals-row value when adding the rows up is wrong for this column
   * (a percentage change). `null` leaves the cell blank.
   */
  aggregate?: (rows: readonly T[]) => number | null;
  cellRenderer?: (params: CellParams<T>) => ReactNode;
  /** Preserve `\n` in the value instead of collapsing to one line. */
  multiline?: boolean;
  /** Hide the filter funnel in the header — for action columns. */
  noFilter?: boolean;
  /**
   * The cell carries its own always-visible control. It is painted as editable
  * but the grid never opens its inline editor over it.
   */
  ownControl?: boolean;
  /**
   * Draw the cell as a type-to-search dropdown. The colourless sibling of a
   * chip column: for long plain-text catalogues. Implies `ownControl`, so leave
   * `editable` off.
   */
  dropdown?: DropdownConfig<T>;
  /**
   * This column absorbs the table's leftover width instead of it being spread
   * across every column, which would stretch narrow action columns too. Lost as
   * soon as the user sets a width by hand.
   */
  flexible?: boolean;
  /**
   * Pin to the right edge so the column stays visible while scrolling
   * horizontally. Honoured only on the trailing run of visible columns.
   */
  pinRight?: boolean;
  /**
   * Title of the merged band grouping adjacent columns that share it, which
   * turns the header into two rows. Filter, sort, rename and resize always live
   * in the lower row; the band is a label.
   */
  headerGroup?: string;
  /** Force the alignment rule instead of letting the grid infer it. */
  cellType?: CellType;
  /** Explicit column alignment override (left, center, right, auto). */
  align?: 'left' | 'center' | 'right' | 'auto' | undefined;
  /** Explicit column text overflow/wrapping mode (wrap, clip, shrink). */
  wrapMode?: 'wrap' | 'clip' | 'shrink' | 'expand' | 'overflow' | undefined;
  /**
   * Keep this column's `cellRenderer` when a formula takes the column over.
   * Off by default: a formula replaces where the value comes from, so a
   * renderer written against the row's own field would draw the wrong thing.
   * Set it when the renderer is a pure function of the computed value — negative
   * stock in red, say.
   */
  preserveRendererWithFormula?: boolean;
  /**
   * The data format the shared settings assigned to this column. Set by the
   * grid from `grid_settings`, not by the page: a column declares what it holds,
   * the organization decides how it is shown.
   */
  dataFormat?: import('./formats').GridDataFormat;
  headerFormat?: import('./formats').GridDataFormat;
  /** Exists only because a formula created it; it has no field in the row. */
  formulaOnly?: boolean;
}

/** TMS called this `ColDef`, after AG Grid. Kept as an alias for ported pages. */
export type ColDef<T> = GridColumn<T>;
